import type { ComponentProps, ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { icons, IconWrapper } from "@/ui";

const buttonVariants = tv({
  base: [
    "relative inline-flex cursor-pointer items-center justify-center gap-2",
    "rounded-lg border border-transparent font-medium",
    "transition-[background-color,border-color,color,box-shadow] duration-150",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none",
    /*
      A faded accent still looks like a live button, so disabled drops to a
      neutral surface rather than relying on opacity alone.
    */
    "disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-active disabled:text-content-subtle disabled:shadow-none",
  ],
  variants: {
    variant: {
      primary: "bg-accent text-content-on-accent shadow-raised hover:bg-accent-hover",
      secondary: "bg-accent-subtle text-accent-on-subtle hover:bg-accent-subtle-hover",
      outline:
        "border-line-control bg-surface text-content shadow-raised hover:border-content-subtle hover:bg-surface-hover",
      tertiaryPlain: "font-normal text-content-muted hover:bg-surface-hover hover:text-content",
      tertiaryAccent: "text-accent-text hover:bg-surface-hover",
      tertiaryFilled: "bg-accent text-content-on-accent shadow-raised hover:bg-accent-hover",
      danger: "bg-danger-fill text-content-on-danger shadow-raised hover:bg-danger",
    },
    size: {
      /* Minimum heights keep every control at a comfortable pointer target. */
      sm: "min-h-8 rounded-md px-3 py-1.5 text-xs tracking-wide",
      md: "min-h-9.5 px-4 py-2 text-sm",
      lg: "min-h-12 px-7 py-3 text-base",
    },
  },
});

export type ButtonProps = {
  isLoading?: boolean;
  left?: ReactNode;
  right?: ReactNode;
} & ComponentProps<"button"> &
  VariantProps<typeof buttonVariants>;

export const Button = ({
  children,
  className,
  disabled = false,
  isLoading = false,
  left,
  right,
  size = "md",
  type = "button",
  variant = "primary",
  ...props
}: ButtonProps) => {
  const loadingContent = isLoading ? "opacity-0" : undefined;

  return (
    <button
      aria-busy={isLoading || undefined}
      className={buttonVariants({ className, variant, size })}
      disabled={disabled || isLoading}
      type={type}
      {...props}
    >
      {/*
        Content stays mounted while loading so the button keeps its width, faded
        rather than `invisible`, which would take the label out of the
        accessibility tree and leave the button unnamed.
      */}
      {left ? (
        <IconWrapper className={loadingContent} size={size}>
          {left}
        </IconWrapper>
      ) : null}

      <span className={loadingContent}>{children}</span>

      {isLoading ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <icons.SpinnerIcon className="size-5" />
        </span>
      ) : null}

      {right ? (
        <IconWrapper className={loadingContent} size={size}>
          {right}
        </IconWrapper>
      ) : null}
    </button>
  );
};
